import React, { useState } from "react";

import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";
import StandartButton from "components/atoms/StandartButton";
import { GenericModal } from "components/GenericModal";
import SwalAlertComp from "components/SwalAlert";

const SendInstructionsModal = ({
    open,
    onClose,
    tracker,
    language,
    onSendInstructions,
}) => {
    const [formData, setFormData] = useState({
        email: "",
        sendToMe: false,
        documentType: "",
    });
    const [isLoading, setIsLoading] = useState(false);

    const getSendDocumentTypes = (sources) => {
        let _sources = [...(sources || [])].filter(
            (x) =>
                x?.source_type?.code !== "androidcapi" &&
                x?.source_type?.code !== "ioscapi",
        );
        if (
            tracker?.document_urls?.app_capi &&
            Array.isArray(tracker?.document_urls?.app_capi) &&
            tracker?.document_urls?.app_capi.length > 0
        ) {
            let _id = tracker?.document_urls?.app_capi[0]
                .toString()
                .split("?source=")[1]
                .split("&")[0];
            _sources.push({
                source_type: {
                    id: parseInt(_id),
                    name: "APP CAPI Guide",
                },
            });
        }
        if (
            tracker?.document_urls?.["app_capi.android"] &&
            Array.isArray(tracker?.document_urls?.["app_capi.android"]) &&
            tracker?.document_urls?.["app_capi.android"].length > 0
        ) {
            let _id = tracker?.document_urls?.["app_capi.android"][0]
                .toString()
                .split("?source=")[1]
                .split("&")[0];
            _sources.push({
                source_type: {
                    id: parseInt(_id),
                    name: "Kotlin APP CAPI Guide",
                },
            });
        }
        if (
            tracker?.document_urls?.["app_capi.ios"] &&
            Array.isArray(tracker?.document_urls?.["app_capi.ios"]) &&
            tracker?.document_urls?.["app_capi.ios"].length > 0
        ) {
            let _id = tracker?.document_urls?.["app_capi.ios"][0]
                .toString()
                .split("?source=")[1]
                .split("&")[0];
            _sources.push({
                source_type: {
                    id: parseInt(_id),
                    name: "Swift APP CAPI Guide",
                },
            });
        }
        return _sources;
    };

    const availableDocumentTypes = getSendDocumentTypes(
        tracker?.tracker?.sources || [],
    );

    const handleInputChange = (field) => (event) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    const handleCheckboxChange = (field) => (event) => {
        setFormData((prev) => ({
            ...prev,
            [field]: event.target.checked,
        }));
    };

    const handleSend = async () => {
        if (!formData.email) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    language?.tracker_wizard?.cannot_be_blank ??
                    "Cannot be blank!",
                icon: "error",
            });
            return;
        }

        setIsLoading(true);

        try {
            const payload = {
                emails: formData.email,
                send_to_me: formData.sendToMe,
                tracker_id: tracker?.tracker?.id,
                type: formData.documentType,
            };

            await onSendInstructions(payload);

            setFormData({
                email: "",
                sendToMe: false,
                documentType: "",
            });
            onClose();
        } catch (err) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: err.message || "Failed to send instructions",
                icon: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setFormData({
                email: "",
                sendToMe: false,
                documentType: "",
            });
            onClose();
        }
    };

    const content = (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {language?.tracker_detail?.send_instruction_text ??
                        "Send implementation instructions to your developer with all necessary documentation and setup guides."}
                </Typography>
            </Box>

            <Stack spacing={3}>
                {availableDocumentTypes.length > 0 && (
                    <FormControl fullWidth>
                        <InputLabel id="send-instructions-document-type-label">
                            {language?.tracker_detail?.document_type ??
                                "Document Type"}
                        </InputLabel>
                        <Select
                            labelId="send-instructions-document-type-label"
                            value={formData.documentType}
                            onChange={handleInputChange("documentType")}
                            disabled={isLoading}
                            label={
                                language?.tracker_detail?.document_type ??
                                "Document Type"
                            }
                        >
                            {availableDocumentTypes.map((item) => (
                                <MenuItem
                                    key={item?.source_type?.id}
                                    value={item?.source_type?.id}
                                >
                                    {item?.source_type?.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <TextField
                    fullWidth
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange("email")}
                    disabled={isLoading}
                    label={
                        language?.send_instructions_modal?.email_address ??
                        "Email address"
                    }
                    placeholder="Enter email"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.sendToMe}
                            onChange={handleCheckboxChange("sendToMe")}
                            disabled={isLoading}
                        />
                    }
                    label={
                        language?.send_instructions_modal
                            ?.send_me_a_copy_of_this_email ??
                        "Send me a copy of this email"
                    }
                />
            </Stack>
        </Box>
    );

    const footer = (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
            }}
        >
            <StandartButton
                onClick={handleClose}
                disabled={isLoading}
                variant={ButtonVariant.BLACK_OUTLINED}
            >
                {language?.buttons?.cancel ?? "Cancel"}
            </StandartButton>
            <StandartButton
                onClick={handleSend}
                disabled={isLoading || !formData.email}
                variant="contained"
                color="primary"
                isLoading={isLoading}
            >
                {language?.buttons?.send ?? "Send"}
            </StandartButton>
        </Box>
    );

    return (
        <GenericModal
            open={open}
            onClose={handleClose}
            title={
                language?.tracker_detail?.send_instructions ??
                "Send Instructions"
            }
            maxWidth="sm"
            content={content}
            footer={footer}
            disableEscapeKeyDown={isLoading}
        />
    );
};

export default SendInstructionsModal;
